<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcFixedAssetRentals',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    fixedAsset: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const sortItems = (...args) => {
    return _.sortBy(
      args[0],
      (p) =>
        (!isStale(p) ? '_' + p.service.name : p.service.name).toLowerCase() +
        p.id
    );
  };
  const isStale = (...args) => {
    const [item] = args;
    return (
      item.inactive ||
      (item.valid_to && $fn.value.parseISODate(item.valid_to) < $fn.value.today)
    );
  };

  const items = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    items: items.value,
    ux: ux.value,
  }));

  const $$load_items = async () => {
    return $fn.value.fetch('list_rental_assets', {
      parameters: { asset: props.fixedAsset.id },
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'items' }, { name: 'ux' }],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: { items, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.fixedAsset,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('fixedAsset');
    }
  );

  const act_8320016629450276_emit_1nrp8mvzp_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_8320016629450276_close_2jo38lvg1_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_8320016629450276_showModal_9lpr6s38n_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'RentalService',
      props: {
        meter: meter,
        assignToRental: true,
        preventEditMeter: true,
      },
    };
  };

  const act_8320016629450276_emit_1nrp8mvzp = new actionTypes.EmitAction({
    actionArgs: act_8320016629450276_emit_1nrp8mvzp_getActionArgs,
  });
  const inst_evh_8320016629450276_close_2jo38lvg1 =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_emit_1nrp8mvzp,
      event: 'close',
      when: inst_evh_8320016629450276_close_2jo38lvg1_getWhen,
      displayName: 'emit',
    });
  const act_8320016629450276_showModal_9lpr6s38n =
    new actionTypes.ShowModalAction({
      actionArgs: act_8320016629450276_showModal_9lpr6s38n_getActionArgs,
      events: [inst_evh_8320016629450276_close_2jo38lvg1],
    });
  const inst_evh_8320016629450276_clickPreventStop_dqqdg2uwi =
    new actionTypes.EventHandlerImpl({
      action: act_8320016629450276_showModal_9lpr6s38n,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_8320016629450276_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8320016629450276_clickPreventStop_dqqdg2uwi],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_8320016629450276_close_2jo38lvg1,
    inst_evh_8320016629450276_clickPreventStop_dqqdg2uwi,
    inst_evh_8320016629450276_clickPreventStop,
  };

  const evh_8320016629450276_close_2jo38lvg1 = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_close_2jo38lvg1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop_dqqdg2uwi = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop_dqqdg2uwi.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8320016629450276_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8320016629450276_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpWidgetCard>
    <template #header>
      <ItpBox alignItems="center">
        <strong>
          {{ pgettext('FixedAssetRentals', 'Rentals') }}
        </strong>
        <ItpButton
          :text="pgettext('FixedAssetRentals', 'Assign to rental...')"
          variant="light"
          size="sm"
          class="ms-auto"
          @click.prevent.stop="
            evh_8320016629450276_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </ItpBox>
    </template>
    <BListGroup v-if="loaded">
      <BListGroupItem v-if="!items.length">
        <span>
          {{ pgettext('FixedAssetRentals', 'No rentals.') }}
        </span>
      </BListGroupItem>
      <BListGroupItem
        :key="item.id"
        class="d-flex flex-column"
        v-for="item in sortItems(items)"
      >
      </BListGroupItem>
    </BListGroup>
  </ItpWidgetCard>
</template>
